import classNames from 'classnames';
import { CheckboxGroup, Form, FormField, SubmitButton, TextAreaInput, TextInput } from 'components/Forms';
import { Heading } from 'components/Typography';
import { TherapyTarget } from 'domains/signup/data/types';
import { MiniScreenerContext, withMiniScreenerMachineState } from 'domains/signupv4/Mini-Screener/context';
import {
  getSpecialtyOptionsAdult,
  getSpecialtyOptionsPediatric,
  getCommunicationConcernOptions,
  OPTION_OTHER,
} from 'domains/signupv4/Mini-Screener/data/areaDetection';
import { TherapyConcerns } from 'domains/signupv4/Mini-Screener/miniScreenerMachine';
import { isArray } from 'lodash';
import { useCallback, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as yup from 'yup';

type EnterConcernsForm = Pick<TherapyConcerns, 'concerns' | 'extraInformation' | 'otherConcerns'>;

const getFormSchema = (t: TFunction): yup.ObjectSchema<EnterConcernsForm> =>
  yup
    .object({
      concerns: yup
        .array()
        .of(yup.string().required('form.validation.fieldIsRequired'))
        .min(1, t('form.validation.fieldIsRequired'))
        .required(t('form.validation.fieldIsRequired')),
      otherConcerns: yup.string().when('concerns', {
        is: (concerns?: string[]) => (isArray(concerns) ? concerns.includes('other') : false),
        then: schema => schema.trim().required(t('form.validation.fieldIsRequired')),
      }),
      extraInformation: yup.string(),
    })
    .required();

type Props = {
  defaultValues?: Partial<TherapyConcerns>;
  onSubmit: SubmitHandler<EnterConcernsForm>;
  target: TherapyTarget;
};

const includesOtherConcern = (concerns?: TherapyConcerns['concerns']) =>
  concerns?.some(concern => concern === OPTION_OTHER) ?? false;

const EnterConcerns: React.FC<Props> = ({ defaultValues, onSubmit, target }) => {
  const { t } = useTranslation();
  const [showOtherField, setShowOtherField] = useState(includesOtherConcern(defaultValues?.concerns));

  const handleOtherCheckboxOption = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === OPTION_OTHER) {
      setShowOtherField(e.target.checked);
    }
  };

  const concernsChild = getSpecialtyOptionsPediatric(t);
  const concernsAdult = getSpecialtyOptionsAdult(t);

  return (
    <>
      <Form onSubmit={onSubmit} schema={getFormSchema(t)} defaultValues={defaultValues} flashErrors={false}>
        <Heading level="h2" className="mb-16">
          {t(`miniScreener.steps.enterConcerns.title`)}
        </Heading>
        <Heading level="h7" as="h3" className="mb-64 sm-d:mb-40">
          {t(`miniScreener.steps.enterConcerns.subtitle`)}
        </Heading>
        <div className="gap-24 mb-64">
          <FormField
            name="concerns"
            component={CheckboxGroup}
            label={t(`miniScreener.steps.enterConcerns.concerns.label.${target === 'child' ? 'child' : 'adult'}`)}
            options={target === 'child' ? concernsChild : concernsAdult}
            onChange={handleOtherCheckboxOption}
            autoFocus
          />

          <FormField
            data-testid="otherConcerns"
            component={TextInput}
            name="otherConcerns"
            label={t(`miniScreener.steps.enterConcerns.otherConcerns`)}
            className={classNames('ml-32 mt-6', {
              hidden: !showOtherField,
            })}
          />
        </div>

        <FormField
          name="extraInformation"
          component={TextAreaInput}
          label={t('miniScreener.steps.enterConcerns.extraInformation')}
          rows={8}
        />
        <SubmitButton
          data-testid="enter-concerns-submit-button"
          className="mt-64 sm-d:mt-40 sm-d:w-full"
          size="lg"
          willRedirect={false}
        >
          {t('miniScreener.steps.enterName.submit')}
        </SubmitButton>
      </Form>
    </>
  );
};

const MiniScreenerWrapper = () => {
  const therapyTarget = MiniScreenerContext.useSelector(({ context }) => context.input.target);
  const prevConcerns = MiniScreenerContext.useSelector(({ context }) => context.data.concerns);
  const { send } = MiniScreenerContext.useActorRef();

  const onSubmit: SubmitHandler<EnterConcernsForm> = useCallback(
    ({ concerns, extraInformation, otherConcerns }) => {
      const fullConcerns = getCommunicationConcernOptions({ currentKeys: concerns, therapyTarget });

      send({
        type: 'submitConcerns',
        concerns,
        fullConcerns,
        extraInformation,
        otherConcerns,
      });
    },
    [send],
  );

  return <EnterConcerns defaultValues={prevConcerns} onSubmit={onSubmit} target={therapyTarget} />;
};

export const MiniScreenerEnterConcerns = withMiniScreenerMachineState(MiniScreenerWrapper, 'enterConcerns');
